import React from 'react';
import { Card } from '../ui';
import { UI_CONSTANTS } from '../../constants/uiConstants';
import { formatPrice, formatReviewCount, combineClasses } from '../../utils/formatters';

const ServiceCard = ({ service, onClick, onBookNow, onLearnMore, className = '', enhanced = false }) => {
  const handleClick = () => {
    if (onClick) onClick(service);
  };

  const handleBookNow = (e) => {
    e.stopPropagation();
    if (onBookNow) {
      onBookNow(service);
    }
  };

  const handleLearnMore = (e) => {
    e.stopPropagation();
    if (onLearnMore) {
      onLearnMore(service);
    } else if (onClick) {
      onClick(service);
    }
  };

  if (enhanced) {
    return (
      <div 
        className={combineClasses(
          "bg-white rounded-xl border border-gray-200 overflow-hidden",
          "hover:shadow-xl cursor-pointer group h-full flex flex-col",
          "transition-all duration-300 ease-in-out",
          "hover:scale-105 hover:border-orange-200",
          className
        )}
        onClick={handleClick}
      >
        {/* Service Image/Icon with Enhanced Gradient */}
        <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 sm:p-6 text-center relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-100/20 to-amber-100/20"></div>
          <div className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded-full px-2 py-1">
            <span className="text-xs font-medium text-orange-600">Popular</span>
          </div>
          
          <div className="relative z-10">
            <div className="text-4xl sm:text-5xl lg:text-6xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-300">
              {service.image}
            </div>
            <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-600">
              <span className="flex items-center space-x-1">
                <span className="text-yellow-500">⭐</span>
                <span className="font-medium">{service.rating}</span>
              </span>
              <span>•</span>
              <span>{formatReviewCount(service.reviewCount)} reviews</span>
            </div>
          </div>
        </div>

        {/* Enhanced Service Details */}
        <div className="p-3 sm:p-4 lg:p-5 flex-1 flex flex-col">
          {/* Category Badge */}
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <span className="bg-orange-100 text-orange-700 px-2 sm:px-3 py-1 rounded-full text-xs font-medium">
              {service.category}
            </span>
            {service.discount && (
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                {service.discount}% OFF
              </span>
            )}
          </div>

          {/* Service Name */}
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight group-hover:text-orange-600 transition-colors">
            {service.name}
          </h3>

          {/* Service Description */}
          {service.description && (
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2 leading-relaxed">
              {service.description}
            </p>
          )}

          {/* Features */}
          {service.features && service.features.length > 0 && (
            <div className="space-y-1 mb-3 sm:mb-4">
              {service.features.slice(0, 2).map((feature, index) => (
                <div key={index} className="flex items-center space-x-2 text-xs text-gray-600">
                  <span className="text-orange-500">✓</span>
                  <span className="truncate">{feature}</span>
                </div>
              ))}
            </div>
          )}

          {/* Duration and Location */}
          <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4 text-xs text-gray-500">
            {service.duration && (
              <div className="flex items-center space-x-1">
                <span>⏱️</span>
                <span className="truncate">{service.duration}</span>
              </div>
            )}
            {service.location && (
              <div className="flex items-center space-x-1">
                <span>📍</span>
                <span className="truncate">{service.location}</span>
              </div>
            )}
          </div>

          {/* Price Section */}
          <div className="flex items-center justify-between mb-3 sm:mb-4 py-2 sm:py-3 border-t border-gray-100">
            <div>
              <div className="text-lg sm:text-xl font-bold text-gray-900">
                {formatPrice(service.price)}
              </div>
              {service.originalPrice && service.originalPrice !== service.price && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs sm:text-sm text-gray-500 line-through">
                    {formatPrice(service.originalPrice)}
                  </span>
                  <span className="text-xs text-green-600 font-medium">
                    Save {Math.round(((service.originalPrice - service.price) / service.originalPrice) * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-auto">
            <button
              onClick={handleLearnMore}
              className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
            >
              Learn More
            </button>
            <button
              onClick={handleBookNow}
              className="px-3 sm:px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Original simple card design
  return (
    <Card 
      className={combineClasses(
        "hover:shadow-xl cursor-pointer group h-full flex flex-col",
        UI_CONSTANTS.TRANSITIONS.DEFAULT,
        className
      )}
      onClick={handleClick}
    >
      <Card.Content className="p-0 flex-1 flex flex-col">
        <div className={combineClasses(
          "h-40 bg-gradient-to-br from-orange-100 to-amber-100",
          "rounded-t-lg flex items-center justify-center text-6xl flex-shrink-0",
          "group-hover:scale-105",
          UI_CONSTANTS.TRANSITIONS.DEFAULT
        )}>
          {service.image}
        </div>
        
        <div className={combineClasses(UI_CONSTANTS.SPACING.SM, "flex-1 flex flex-col justify-between")}>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className={combineClasses(
                "bg-orange-100 text-orange-700 px-2 py-1",
                UI_CONSTANTS.RADIUS.FULL,
                UI_CONSTANTS.TYPOGRAPHY.SMALL
              )}>
                {service.category}
              </span>
              
              <div className="flex items-center space-x-1">
                <span className="text-yellow-500">⭐</span>
                <span className={combineClasses(
                  "font-medium",
                  UI_CONSTANTS.TYPOGRAPHY.CAPTION
                )}>
                  {service.rating}
                </span>
                <span className={combineClasses(
                  "text-gray-500",
                  UI_CONSTANTS.TYPOGRAPHY.CAPTION
                )}>
                  ({formatReviewCount(service.reviewCount)})
                </span>
              </div>
            </div>
            
            <h3 className={combineClasses(
              "font-semibold text-gray-800 mb-2 leading-tight",
              UI_CONSTANTS.TYPOGRAPHY.BODY
            )}>
              {service.name}
            </h3>
          </div>
          
          <div className="flex items-center space-x-2 mt-auto">
            <span className={combineClasses(
              "font-bold text-gray-900",
              UI_CONSTANTS.TYPOGRAPHY.HEADING_3
            )}>
              {formatPrice(service.price)}
            </span>
            <span className={combineClasses(
              "text-gray-500 line-through",
              UI_CONSTANTS.TYPOGRAPHY.CAPTION
            )}>
              {formatPrice(service.originalPrice)}
            </span>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ServiceCard;
